type GoogleAnalyticsFunction = (
  command: 'event',
  eventName: 'page_view',
  parameters: Record<string, string>,
) => void;

declare global {
  interface Window {
    gtag?: GoogleAnalyticsFunction;
  }
}

const SENSITIVE_PATHS: Array<[RegExp, string]> = [
  [/^\/join\/team\/[^/]+$/, '/join/team/:token'],
  [/^\/join\/[^/]+$/, '/join/:code'],
  [/^\/invite\/[^/]+$/, '/invite/:token'],
  [/^\/s\/[^/]+\/session\/[^/]+$/, '/s/:token/session/:attemptId'],
  [/^\/s\/[^/]+$/, '/s/:token'],
  [/^\/r\/[^/]+$/, '/r/:resultToken'],
];

export const ANALYTICS_UTM_KEYS: readonly string[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

const UTM_VALUE_MAX_LENGTH = 100;

// Snapshot of the entry URL's query, taken when this module first loads. The
// router's <Navigate replace> redirects rewrite window.location in a child
// effect before App's mount effect runs, so reading it there loses the UTMs.
export const INITIAL_LOCATION_SEARCH: string =
  typeof window === 'undefined' ? '' : window.location.search;

export const GOOGLE_ANALYTICS_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID ?? '';

export function isGoogleAnalyticsMeasurementId(value: string | undefined): boolean {
  return /^G-[A-Z0-9]+$/.test(value ?? '');
}

export function normalizeAnalyticsPath(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  const sensitiveRoute = SENSITIVE_PATHS.find(([pattern]) => pattern.test(pathname));

  return sensitiveRoute?.[1] ?? pathname;
}

export function pickUtmQuery(search: string): string {
  const params = new URLSearchParams(search);
  const picked = new URLSearchParams();

  ANALYTICS_UTM_KEYS.forEach((key) => {
    const value = params.get(key)?.slice(0, UTM_VALUE_MAX_LENGTH).trim();
    if (value) {
      picked.set(key, value);
    }
  });

  const query = picked.toString();
  return query ? `?${query}` : '';
}

export function trackPageView(measurementId: string, path: string): void {
  if (!isGoogleAnalyticsMeasurementId(measurementId) || typeof window.gtag !== 'function') {
    return;
  }

  const pagePath = normalizeAnalyticsPath(path);
  const queryStart = path.indexOf('?');
  const query = queryStart === -1 ? '' : path.slice(queryStart).split('#', 1)[0];

  window.gtag('event', 'page_view', {
    page_location: `${window.location.origin}${pagePath}${pickUtmQuery(query)}`,
    page_path: pagePath,
    page_title: document.title,
  });
}
